import React, { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { BookDown, ChevronDown, Loader2 } from "lucide-react";

export interface PdfExportOptions {
  sections: {
    overview: boolean;
    studyTime: boolean;
    flashcards: boolean;
    quizzes: boolean;
    exams: boolean;
  };
  dateRange: "7days" | "30days" | "90days" | "all";
  styling: {
    colorScheme: "default" | "minimal" | "vibrant";
    includeCharts: boolean;
  };
}

interface PdfExportDropdownProps {
  onExport: (options: PdfExportOptions) => void;
  isExporting: boolean;
  variant?: "desktop" | "mobile";
}

const defaultOptions: PdfExportOptions = {
  sections: {
    overview: true,
    studyTime: true,
    flashcards: true,
    quizzes: true,
    exams: true,
  },
  dateRange: "all",
  styling: {
    colorScheme: "default",
    includeCharts: false,
  },
};

const dateRangeLabels: Record<PdfExportOptions["dateRange"], string> = {
  "7days": "Last 7 days",
  "30days": "Last 30 days",
  "90days": "Last 90 days",
  all: "All time",
};

const colorSchemeLabels: Record<PdfExportOptions["styling"]["colorScheme"], string> = {
  default: "Classic",
  minimal: "Minimal",
  vibrant: "Vibrant",
};

export const PdfExportDropdown: React.FC<PdfExportDropdownProps> = ({
  onExport,
  isExporting,
  variant = "desktop",
}) => {
  type PresetKey = "full" | "overview" | "flashcards" | "quizzes" | "exams";

  const [options, setOptions] = useState<PdfExportOptions>(defaultOptions);

  const handleSectionToggle = (section: keyof PdfExportOptions["sections"]) => {
    setOptions((prev) => ({
      ...prev,
      sections: {
        ...prev.sections,
        [section]: !prev.sections[section],
      },
    }));
  };

  const handleDateRangeChange = (value: string) => {
    setOptions((prev) => ({
      ...prev,
      dateRange: value as PdfExportOptions["dateRange"],
    }));
  };

  const handleColorSchemeChange = (value: string) => {
    setOptions((prev) => ({
      ...prev,
      styling: {
        ...prev.styling,
        colorScheme: value as PdfExportOptions["styling"]["colorScheme"],
      },
    }));
  };

  const handleExport = () => {
    onExport(options);
  };

  const applyPreset = (preset: PresetKey) => {
    const baseSections = {
      overview: false,
      studyTime: false,
      flashcards: false,
      quizzes: false,
      exams: false,
    };

    let presetSections: PdfExportOptions["sections"];

    switch (preset) {
      case "full":
        presetSections = {
          overview: true,
          studyTime: true,
          flashcards: true,
          quizzes: true,
          exams: true,
        };
        break;
      case "overview":
        presetSections = {
          ...baseSections,
          overview: true,
        };
        break;
      case "flashcards":
        presetSections = {
          ...baseSections,
          flashcards: true,
        };
        break;
      case "quizzes":
        presetSections = {
          ...baseSections,
          quizzes: true,
        };
        break;
      case "exams":
        presetSections = {
          ...baseSections,
          exams: true,
        };
        break;
      default:
        presetSections = defaultOptions.sections;
        break;
    }

    setOptions((prev) => {
      const next = {
        ...prev,
        sections: presetSections,
      };
      onExport(next);
      return next;
    });
  };

  const selectedSectionsCount = Object.values(options.sections).filter(Boolean).length;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {variant === "mobile" ? (
          <Button
            size="icon"
            className="h-9 w-9"
            aria-label="Download report"
            disabled={isExporting}
          >
            {isExporting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <BookDown className="h-4 w-4" />
            )}
          </Button>
        ) : (
          <Button className="h-9 gap-2" disabled={isExporting}>
            {isExporting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                Download
                <ChevronDown className="h-4 w-4" />
              </>
            )}
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 z-50 bg-popover">
        <DropdownMenuLabel>Export Options</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* Quick presets */}
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          Quick presets
        </DropdownMenuLabel>
        <DropdownMenuItem
          disabled={isExporting}
          onClick={() => applyPreset("full")}
        >
          Full Report
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={isExporting}
          onClick={() => applyPreset("overview")}
        >
          Overview Only
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={isExporting}
          onClick={() => applyPreset("flashcards")}
        >
          Flashcards Only
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={isExporting}
          onClick={() => applyPreset("quizzes")}
        >
          Quizzes Only
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={isExporting}
          onClick={() => applyPreset("exams")}
        >
          Exams Only
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Sections */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            Sections ({selectedSectionsCount}/5)
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent className="z-50 bg-popover">
              <DropdownMenuCheckboxItem
                checked={options.sections.overview}
                onCheckedChange={() => handleSectionToggle("overview")}
              >
                Overview Statistics
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={options.sections.studyTime}
                onCheckedChange={() => handleSectionToggle("studyTime")}
              >
                Study Time
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={options.sections.flashcards}
                onCheckedChange={() => handleSectionToggle("flashcards")}
              >
                Flashcard Decks
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={options.sections.quizzes}
                onCheckedChange={() => handleSectionToggle("quizzes")}
              >
                Quiz Attempts
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                checked={options.sections.exams}
                onCheckedChange={() => handleSectionToggle("exams")}
              >
                Exam Attempts
              </DropdownMenuCheckboxItem>
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        {/* Date Range */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            Date Range: {dateRangeLabels[options.dateRange]}
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent className="z-50 bg-popover">
              <DropdownMenuRadioGroup
                value={options.dateRange}
                onValueChange={handleDateRangeChange}
              >
                <DropdownMenuRadioItem value="7days">Last 7 days</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="30days">Last 30 days</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="90days">Last 90 days</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="all">All time</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        {/* Styling */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            Style: {colorSchemeLabels[options.styling.colorScheme]}
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent className="z-50 bg-popover">
              <DropdownMenuRadioGroup
                value={options.styling.colorScheme}
                onValueChange={handleColorSchemeChange}
              >
                <DropdownMenuRadioItem value="default">Classic</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="minimal">Minimal</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="vibrant">Vibrant</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        <DropdownMenuSeparator />

        {/* Export Button */}
        <div className="p-2">
          <Button
            onClick={handleExport}
            className="w-full gap-2"
            disabled={isExporting || selectedSectionsCount === 0}
          >
            {isExporting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <BookDown className="h-4 w-4" />
                Export PDF
              </>
            )}
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
